import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { icons } from '../components/ui/Icons';
import Button from '../components/ui/Button';
import AddSlotModal from '../components/AddSlotModal';
import BulkSlotModal from '../components/BulkSlotModal';
import GoogleCalendarConnect from '../components/GoogleCalendarConnect';
import PaymentModal from '../components/PaymentModal';
import { useAppContext } from '../context/AppContext';
import { deleteSlot } from '../services/slotService';
import { getWaitlistDetailsByBarbershop } from '../services/waitlistService';
import { useAutoExpiration } from '../hooks/useAutoExpiration';
import { createPayment } from '../services/paymentService';
import {
  openWhatsApp,
  msgBarbershopConfirmsBooking,
  msgServiceCompleted,
  msgClientNoShow,
  msgBarbershopReleasesSlot,
  msgReleasedSlotExpired,
} from '../services/whatsappService';
import toast from 'react-hot-toast';

/**
 * Página: BarbershopOwnerPage (/pages/BarbershopOwnerPage.jsx)
 * Página de uma barbearia específica (Visão do Dono)
 */
const BarbershopOwnerPage = () => {
  const navigate = useNavigate();
  const { currentUser, barbershops, appointments, availableSlots, updateAppointmentStatus, addSlot } = useAppContext();
  
  // Suporta formato antigo (barbershopId) e novo (barbershops array)
  const shop = useMemo(() => {
    if (!currentUser) return null;
    const barbershopId = currentUser.barbershopId || (currentUser.barbershops && currentUser.barbershops[0]);
    return barbershops.find(b => b.id === barbershopId);
  }, [barbershops, currentUser]);

  const [activeTab, setActiveTab] = useState('pending'); // 'pending' | 'confirmed' | 'history' | 'slots' | 'waitlist' | 'analytics' | 'integrations'
  const [isAddSlotModalOpen, setIsAddSlotModalOpen] = useState(false);
  const [isBulkSlotModalOpen, setIsBulkSlotModalOpen] = useState(false);
  const [waitlistData, setWaitlistData] = useState({});
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [appointmentToComplete, setAppointmentToComplete] = useState(null);

  const pendingAppointments = useMemo(() =>
    appointments
      .filter(a => a.barbershopId === shop?.id && a.status === 'pending')
      .sort((a, b) => new Date(a.date + 'T' + a.time) - new Date(b.date + 'T' + b.time)),
    [appointments, shop]
  );

  const confirmedAppointments = useMemo(() =>
    appointments
      .filter(a => a.barbershopId === shop?.id && a.status === 'confirmed')
      .sort((a, b) => new Date(a.date + 'T' + a.time) - new Date(b.date + 'T' + b.time)),
    [appointments, shop]
  );

  const historyAppointments = useMemo(() =>
    appointments
      .filter(a => a.barbershopId === shop?.id && (a.status === 'completed' || a.status === 'cancelled' || a.status === 'no-show'))
      .sort((a, b) => new Date(b.date + 'T' + b.time) - new Date(a.date + 'T' + a.time)),
    [appointments, shop]
  );

  // Hook para expiração automática (verifica ao montar + a cada 12h)
  useAutoExpiration(appointments, availableSlots);
  
  // Carregar waitlist quando a aba estiver ativa
  useEffect(() => {
    const loadWaitlist = async () => {
      if (activeTab === 'waitlist' && shop?.id) {
        const data = await getWaitlistDetailsByBarbershop(shop.id);
        setWaitlistData(data);
      }
    };
    
    loadWaitlist();
  }, [activeTab, shop?.id]);

  // Agrupar slots por data
  const slotsByDate = useMemo(() => {
    const grouped = {};
    availableSlots
      .filter(slot => !slot.isBooked)
      .forEach(slot => {
        if (!grouped[slot.date]) {
          grouped[slot.date] = [];
        }
        grouped[slot.date].push(slot);
      });
    
    // Ordenar datas
    Object.keys(grouped).forEach(date => {
      grouped[date].sort((a, b) => a.time.localeCompare(b.time));
    });
    
    return grouped;
  }, [availableSlots]);

  // Analytics: Estatísticas da barbearia
  const analytics = useMemo(() => {
    if (!shop) return null;

    const shopAppointments = appointments.filter(a => a.barbershopId === shop.id);
    const completed = shopAppointments.filter(a => a.status === 'completed');
    const cancelled = shopAppointments.filter(a => a.status === 'cancelled');

    // Receita total
    const totalRevenue = completed.reduce((sum, app) => {
      return sum + (Number(app.servicePrice) || 0);
    }, 0);

    // Receita média por atendimento
    const avgRevenue = completed.length > 0 ? totalRevenue / completed.length : 0;

    // Taxa de cancelamento
    const cancellationRate = shopAppointments.length > 0
      ? ((cancelled.length / shopAppointments.length) * 100).toFixed(1)
      : 0;

    // Serviço mais popular (por quantidade)
    const serviceCounts = {};
    completed.forEach(app => {
      serviceCounts[app.service] = (serviceCounts[app.service] || 0) + 1;
    });
    const mostPopularService = Object.keys(serviceCounts).length > 0
      ? Object.entries(serviceCounts).sort((a, b) => b[1] - a[1])[0]
      : null;

    // Serviço mais rentável
    const serviceRevenue = {};
    completed.forEach(app => {
      const revenue = Number(app.servicePrice) || 0;
      serviceRevenue[app.service] = (serviceRevenue[app.service] || 0) + revenue;
    });
    const mostProfitableService = Object.keys(serviceRevenue).length > 0
      ? Object.entries(serviceRevenue).sort((a, b) => b[1] - a[1])[0]
      : null;

    // Horários mais populares (agrupados por hora)
    const hourCounts = {};
    completed.forEach(app => {
      const hour = app.time.split(':')[0];
      hourCounts[hour] = (hourCounts[hour] || 0) + 1;
    });
    const peakHours = Object.entries(hourCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([hour, count]) => ({ hour: `${hour}:00`, count }));

    // Agendamentos dos últimos 6 meses
    const now = new Date();
    const monthlyData = {};
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthlyData[monthKey] = {
        completed: 0,
        cancelled: 0,
        revenue: 0
      };
    }

    shopAppointments.forEach(app => {
      const [y, m] = app.date.split('-').map(Number);
      const monthKey = `${y}-${String(m).padStart(2, '0')}`;
      
      if (monthlyData[monthKey]) {
        if (app.status === 'completed') {
          monthlyData[monthKey].completed++;
          monthlyData[monthKey].revenue += Number(app.servicePrice) || 0;
        } else if (app.status === 'cancelled') {
          monthlyData[monthKey].cancelled++;
        }
      }
    });

    const monthlyAppointments = Object.entries(monthlyData).map(([month, data]) => ({
      month,
      monthName: new Date(month + '-01').toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
      ...data
    }));

    return {
      totalAppointments: shopAppointments.length,
      completed: completed.length,
      cancelled: cancelled.length,
      pending: pendingAppointments.length,
      confirmed: confirmedAppointments.length,
      totalRevenue,
      avgRevenue,
      cancellationRate,
      mostPopularService,
      mostProfitableService,
      peakHours,
      monthlyAppointments
    };
  }, [appointments, shop, pendingAppointments.length, confirmedAppointments.length]);

  const handleDecision = (appointmentId, status) => {
    updateAppointmentStatus(appointmentId, status);
  };

  /**
   * Abre o modal de pagamento antes de concluir o agendamento.
   */
  const handleOpenPaymentModal = (appointment) => {
    setAppointmentToComplete(appointment);
    setIsPaymentModalOpen(true);
  };

  /**
   * Chamado quando o barbeiro confirma os dados de pagamento.
   * Salva o pagamento no Firestore e marca o agendamento como concluído.
   */
  const handlePaymentConfirm = async (paymentData) => {
    try {
      const result = await createPayment(paymentData);
      if (!result.success) {
        toast.error('Erro ao registrar pagamento: ' + result.error);
        return;
      }
      // Marca o agendamento como concluído
      await updateAppointmentStatus(appointmentToComplete.id, 'completed');
      setIsPaymentModalOpen(false);
      // Notifica o cliente via WhatsApp (se tiver número cadastrado)
      if (appointmentToComplete.clientPhone) {
        openWhatsApp(
          appointmentToComplete.clientPhone,
          msgServiceCompleted({
            clientName: appointmentToComplete.clientName,
            service: appointmentToComplete.service,
            paymentMethod: paymentData.paymentMethod,
            cardType: paymentData.cardType,
          })
        );
      }
      setAppointmentToComplete(null);
      toast.success('Atendimento concluído e pagamento registrado! 💰');
    } catch (err) {
      console.error('Erro ao salvar pagamento:', err);
      toast.error('Erro inesperado ao registrar pagamento');
    }
  };
  
  const handleAddSlot = async (formData) => {
    const result = await addSlot(formData);
    if (result?.success) {
      toast.success('Horário adicionado com sucesso!');
      setIsAddSlotModalOpen(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Tem certeza que deseja deletar este horário?')) return;
    
    const result = await deleteSlot(slotId);
    if (result.success) {
      toast.success('Horário deletado com sucesso!');
    } else {
      toast.error('Erro ao deletar horário');
    }
  };

  const handleViewAsClient = () => {
    navigate(`/barbershop/${shop.id}?clientView=true`);
  };

  const handleBulkAddSlots = async (slots) => {
    let successCount = 0;
    let errorCount = 0;

    for (const slot of slots) {
      const result = await addSlot({
        ...slot,
        barbershopId: shop.id
      });
      
      if (result?.success) {
        successCount++;
      } else {
        errorCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} horário(s) criado(s) com sucesso!`);
    }
    if (errorCount > 0) {
      toast.error(`Erro ao criar ${errorCount} horário(s)`);
    }

    setIsBulkSlotModalOpen(false);
  };

  // Loading ou Barbearia não encontrada
  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <p className="text-gray-600">Carregando...</p>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
        <p className="text-xl text-gray-600 mb-4">Barbearia não encontrada.</p>
        <p className="text-sm text-gray-500 mb-6">Você não está associado a nenhuma barbearia.</p>
        <Button onClick={() => navigate('/')}>Voltar para Home</Button>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <h1 className="text-4xl font-bold text-gray-900">Gerenciamento: {shop.name}</h1>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => navigate('/barbershop/edit')}>
              {icons.pencil({ className: 'h-5 w-5 inline mr-2' })}
              Editar Barbearia
            </Button>
            <Button variant="secondary" onClick={handleViewAsClient}>
              {icons.user({ className: 'h-5 w-5 inline mr-2' })}
              Ver como Cliente
            </Button>
          </div>
        </div>

        {/* Navegação em Abas */}
        <div className="border-b border-gray-300 mb-8 overflow-x-auto">
          <nav className="flex space-x-2 md:space-x-4 min-w-max md:min-w-0">
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-3 md:px-4 py-2 font-semibold rounded-t-lg transition-all whitespace-nowrap text-sm md:text-base ${
                activeTab === 'pending'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Solicitações ({pendingAppointments.length})
            </button>
            <button
              onClick={() => setActiveTab('confirmed')}
              className={`px-3 md:px-4 py-2 font-semibold rounded-t-lg transition-all whitespace-nowrap text-sm md:text-base ${
                activeTab === 'confirmed'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Confirmados ({confirmedAppointments.length})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-3 md:px-4 py-2 font-semibold rounded-t-lg transition-all whitespace-nowrap text-sm md:text-base ${
                activeTab === 'history'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Histórico
            </button>
            <button
              onClick={() => setActiveTab('slots')}
              className={`px-3 md:px-4 py-2 font-semibold rounded-t-lg transition-all whitespace-nowrap text-sm md:text-base ${
                activeTab === 'slots'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Horários
            </button>
            <button
              onClick={() => setActiveTab('waitlist')}
              className={`px-3 md:px-4 py-2 font-semibold rounded-t-lg transition-all whitespace-nowrap text-sm md:text-base ${
                activeTab === 'waitlist'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              🔔 Fila de Espera
            </button>
            <button
              onClick={() => navigate('/analytics')}
              className="px-3 md:px-4 py-2 font-semibold rounded-t-lg transition-all whitespace-nowrap text-sm md:text-base text-gray-500 hover:text-indigo-600"
            >
              📊 Analytics
            </button>
            <button
              onClick={() => setActiveTab('integrations')}
              className={`px-3 md:px-4 py-2 font-semibold rounded-t-lg transition-all whitespace-nowrap text-sm md:text-base ${
                activeTab === 'integrations'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              🔗 Integrações
            </button>
          </nav>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Coluna Principal: Conteúdo das Abas */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* ABA: Solicitações Pendentes */}
            {activeTab === 'pending' && (
              <div className="bg-white rounded-xl shadow-xl p-6 md:p-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Solicitações de Agendamento</h2>
                  <span className="bg-indigo-100 text-indigo-800 text-sm font-semibold px-3 py-1 rounded-full">
                    {pendingAppointments.length} Pendente(s)
                  </span>
                </div>
              
                {pendingAppointments.length > 0 ? (
                  <div className="space-y-4">
                    {pendingAppointments.map(app => (
                      <div key={app.id} className="border border-gray-200 rounded-lg p-4 transition-all hover:shadow-md">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                          <div className="flex-1">
                            <p className="text-lg font-semibold text-gray-900">{app.clientName}</p>
                            <p className="text-sm text-gray-600 mb-2">{app.service}</p>
                            <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                              <div className="flex items-center">
                                {icons.calendar({ className: 'h-4 w-4 mr-1.5 text-indigo-600' })}
                                {new Date(app.date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                              </div>
                              <div className="flex items-center">
                                {icons.clock({ className: 'h-4 w-4 mr-1.5 text-indigo-600' })}
                                {app.time}
                              </div>
                            </div>
                            {(app.servicePrice || app.serviceDuration) && (
                              <div className="flex items-center space-x-3 mb-2">
                                {app.servicePrice && (
                                  <div className="flex items-center bg-green-50 border border-green-200 rounded px-2 py-1">
                                    <span className="text-green-700 font-semibold text-sm">
                                      💰 R$ {typeof app.servicePrice === 'number' ? app.servicePrice.toFixed(2) : app.servicePrice}
                                    </span>
                                  </div>
                                )}
                                {app.serviceDuration && (
                                  <div className="flex items-center bg-blue-50 border border-blue-200 rounded px-2 py-1">
                                    <span className="text-blue-700 text-sm">
                                      ⏱️ {app.serviceDuration}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                            {app.clientPhone && (
                              <p className="text-sm text-gray-600">
                                📱 {app.clientPhone}
                              </p>
                            )}
                            {app.notes && (
                              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                                <p className="text-xs font-semibold text-yellow-800 mb-1">Observação:</p>
                                <p className="text-sm text-gray-700">{app.notes}</p>
                              </div>
                            )}
                          </div>
                          <div className="flex space-x-2 mt-3 md:mt-0 md:ml-4">
                            <Button variant="danger" className="py-1 px-3 text-sm" onClick={() => handleDecision(app.id, 'cancelled')}>
                              {icons.xCircle({ className: 'h-4 w-4 mr-1 hidden sm:inline' })}
                              Recusar
                            </Button>
                            <Button variant="success" className="py-1 px-3 text-sm" onClick={() => handleDecision(app.id, 'confirmed')}>
                              {icons.check({ className: 'h-4 w-4 mr-1 hidden sm:inline' })}
                              Aceitar
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Nenhuma solicitação pendente no momento.</p>
                )}
              </div>
            )}

            {/* ABA: Confirmados (Aceitos mas não concluídos) */}
            {activeTab === 'confirmed' && (
              <div className="bg-white rounded-xl shadow-xl p-6 md:p-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Agendamentos Confirmados</h2>
                  <span className="bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full">
                    {confirmedAppointments.length} Confirmado(s)
                  </span>
                </div>
              
                {confirmedAppointments.length > 0 ? (
                  <div className="space-y-4">
                    {confirmedAppointments.map(app => (
                      <div key={app.id} className="border border-blue-200 bg-blue-50 rounded-lg p-4 transition-all hover:shadow-md">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                          <div className="flex-1">
                            <p className="text-lg font-semibold text-gray-900">{app.clientName}</p>
                            <p className="text-sm text-gray-600 mb-2">{app.service}</p>
                            <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                              <div className="flex items-center">
                                {icons.calendar({ className: 'h-4 w-4 mr-1.5 text-blue-600' })}
                                {new Date(app.date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                              </div>
                              <div className="flex items-center">
                                {icons.clock({ className: 'h-4 w-4 mr-1.5 text-blue-600' })}
                                {app.time}
                              </div>
                            </div>
                            {(app.servicePrice || app.serviceDuration) && (
                              <div className="flex items-center space-x-3 mb-2">
                                {app.servicePrice && (
                                  <div className="flex items-center bg-green-50 border border-green-200 rounded px-2 py-1">
                                    <span className="text-green-700 font-semibold text-sm">
                                      💰 R$ {typeof app.servicePrice === 'number' ? app.servicePrice.toFixed(2) : app.servicePrice}
                                    </span>
                                  </div>
                                )}
                                {app.serviceDuration && (
                                  <div className="flex items-center bg-blue-50 border border-blue-200 rounded px-2 py-1">
                                    <span className="text-blue-700 text-sm">
                                      ⏱️ {app.serviceDuration}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                            {app.clientPhone && (
                              <p className="text-sm text-gray-600">
                                📱 {app.clientPhone}
                              </p>
                            )}
                            {app.notes && (
                              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                                <p className="text-xs font-semibold text-yellow-800 mb-1">Observação:</p>
                                <p className="text-sm text-gray-700">{app.notes}</p>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col space-y-2 mt-3 md:mt-0 md:ml-4">
                            {/* Notificar confirmação via WhatsApp */}
                            {app.clientPhone && (
                              <button
                                type="button"
                                onClick={() => openWhatsApp(
                                  app.clientPhone,
                                  msgBarbershopConfirmsBooking({
                                    clientName: app.clientName,
                                    service: app.service,
                                    date: app.date,
                                    time: app.time,
                                  })
                                )}
                                className="flex items-center justify-center gap-1.5 py-1 px-3 text-sm font-medium bg-green-100 text-green-700 border border-green-300 rounded-lg hover:bg-green-200 transition-colors"
                                title="Enviar confirmação de agendamento ao cliente via WhatsApp"
                              >
                                <span className="font-bold">WA</span> Confirmar
                              </button>
                            )}
                            <Button variant="success" className="py-1 px-3 text-sm" onClick={() => handleOpenPaymentModal(app)}>
                              {icons.check({ className: 'h-4 w-4 mr-1' })}
                              💰 Concluído
                            </Button>
                            <Button
                              variant="danger"
                              className="py-1 px-3 text-sm"
                              onClick={() => {
                                handleDecision(app.id, 'no-show');
                                if (app.clientPhone) {
                                  openWhatsApp(
                                    app.clientPhone,
                                    msgClientNoShow({
                                      clientName: app.clientName,
                                      date: app.date,
                                      time: app.time,
                                    })
                                  );
                                }
                              }}
                            >
                              {icons.xCircle({ className: 'h-4 w-4 mr-1' })}
                              Não Compareceu
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Nenhum agendamento confirmado no momento.</p>
                )}
              </div>
            )}

            {/* ABA: Histórico (Completos e Cancelados) */}
            {activeTab === 'history' && (
              <div className="bg-white rounded-xl shadow-xl p-6 md:p-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Histórico de Agendamentos</h2>
                  <span className="bg-gray-100 text-gray-800 text-sm font-semibold px-3 py-1 rounded-full">
                    {historyAppointments.length} Registro(s)
                  </span>
                </div>
              
                {historyAppointments.length > 0 ? (
                  <div className="space-y-4">
                    {historyAppointments.map(app => (
                      <div key={app.id} className={`border rounded-lg p-4 transition-all ${
                        app.status === 'completed' ? 'border-green-200 bg-green-50' : 
                        app.status === 'no-show' ? 'border-orange-200 bg-orange-50' :
                        'border-red-200 bg-red-50'
                      }`}>
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <p className="text-lg font-semibold text-gray-900">{app.clientName}</p>
                              <span className={`ml-3 px-2 py-0.5 text-xs font-semibold rounded ${
                                app.status === 'completed' 
                                  ? 'bg-green-200 text-green-800' 
                                  : app.status === 'no-show'
                                  ? 'bg-orange-200 text-orange-800'
                                  : 'bg-red-200 text-red-800'
                              }`}>
                                {app.status === 'completed' ? 'Concluído' : 
                                 app.status === 'no-show' ? 'Não Compareceu' :
                                 'Cancelado'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{app.service}</p>
                            <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                              <div className="flex items-center">
                                {icons.calendar({ className: 'h-4 w-4 mr-1.5 text-gray-600' })}
                                {new Date(app.date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                              </div>
                              <div className="flex items-center">
                                {icons.clock({ className: 'h-4 w-4 mr-1.5 text-gray-600' })}
                                {app.time}
                              </div>
                            </div>
                            {(app.servicePrice || app.serviceDuration) && (
                              <div className="flex items-center space-x-3 mb-2">
                                {app.servicePrice && (
                                  <div className="flex items-center bg-green-50 border border-green-200 rounded px-2 py-1">
                                    <span className="text-green-700 font-semibold text-sm">
                                      💰 R$ {typeof app.servicePrice === 'number' ? app.servicePrice.toFixed(2) : app.servicePrice}
                                    </span>
                                  </div>
                                )}
                                {app.serviceDuration && (
                                  <div className="flex items-center bg-blue-50 border border-blue-200 rounded px-2 py-1">
                                    <span className="text-blue-700 text-sm">
                                      ⏱️ {app.serviceDuration}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                            {app.clientPhone && (
                              <p className="text-sm text-gray-600">
                                📱 {app.clientPhone}
                              </p>
                            )}
                            {app.notes && (
                              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                                <p className="text-xs font-semibold text-yellow-800 mb-1">Observação:</p>
                                <p className="text-sm text-gray-700">{app.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Nenhum registro no histórico.</p>
                )}
              </div>
            )}

            {/* ABA: Horários Disponíveis */}
            {activeTab === 'slots' && (
              <div className="bg-white rounded-xl shadow-xl p-6 md:p-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Horários Disponíveis</h2>
                  <span className="bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded-full">
                    {availableSlots.filter(s => !s.isBooked).length} Vago(s)
                  </span>
                </div>

                {Object.keys(slotsByDate).length > 0 ? (
                  <div className="space-y-6">
                    {Object.entries(slotsByDate)
                      .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
                      .map(([date, slots]) => (
                        <div key={date} className="border border-gray-200 rounded-lg p-4">
                          <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                            {icons.calendar({ className: 'h-5 w-5 mr-2 text-indigo-600' })}
                            {new Date(date + 'T12:00:00').toLocaleDateString('pt-BR', { 
                              weekday: 'long', 
                              day: '2-digit', 
                              month: 'long' 
                            })}
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {slots.map(slot => (
                              <div
                                key={slot.id}
                                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-green-50 text-green-700 text-sm font-medium border border-green-200 group relative"
                              >
                                {icons.clock({ className: 'h-4 w-4 mr-1.5' })}
                                {slot.time}
                                <button
                                  onClick={() => handleDeleteSlot(slot.id)}
                                  className="ml-2 text-red-500 hover:text-red-700 transition-colors"
                                  title="Deletar horário"
                                >
                                  {icons.xCircle({ className: 'h-4 w-4' })}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500 mb-4">Nenhum horário disponível cadastrado.</p>
                    <Button onClick={() => setIsAddSlotModalOpen(true)}>
                      {icons.plus({ className: 'h-5 w-5 mr-2 inline' })}
                      Cadastrar Horário
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* ABA: Analytics */}
            {activeTab === 'analytics' && analytics && (
              <div className="space-y-6">
                {/* Cards de Estatísticas Principais */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Total Agendamentos</p>
                        <p className="text-3xl font-bold text-gray-900">{analytics.totalAppointments}</p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                        {icons.calendar({ className: 'h-6 w-6 text-indigo-600' })}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-md p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Concluídos</p>
                        <p className="text-3xl font-bold text-green-600">{analytics.completed}</p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                        {icons.check({ className: 'h-6 w-6 text-green-600' })}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-md p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Receita Total</p>
                        <p className="text-2xl font-bold text-green-600">
                          {analytics.totalRevenue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                        <span className="text-2xl">💰</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-md p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Taxa Cancelamento</p>
                        <p className="text-3xl font-bold text-red-600">{analytics.cancellationRate}%</p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                        {icons.x({ className: 'h-6 w-6 text-red-600' })}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-md p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Receita Média</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {analytics.avgRevenue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-2xl">📊</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-md p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Cancelados</p>
                        <p className="text-3xl font-bold text-red-600">{analytics.cancelled}</p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                        {icons.x({ className: 'h-6 w-6 text-red-600' })}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-md p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Pendentes</p>
                        <p className="text-3xl font-bold text-yellow-600">{analytics.pending}</p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                        {icons.clock({ className: 'h-6 w-6 text-yellow-600' })}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-md p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Confirmados</p>
                        <p className="text-3xl font-bold text-blue-600">{analytics.confirmed}</p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        {icons.check({ className: 'h-6 w-6 text-blue-600' })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Serviços Populares e Rentáveis */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">🏆 Serviço Mais Popular</h3>
                    {analytics.mostPopularService ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xl font-bold text-purple-600">{analytics.mostPopularService[0]}</p>
                          <p className="text-sm text-gray-600">{analytics.mostPopularService[1]} atendimentos</p>
                        </div>
                        <span className="text-4xl">✂️</span>
                      </div>
                    ) : (
                      <p className="text-gray-500">Nenhum dado disponível</p>
                    )}
                  </div>

                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">💎 Serviço Mais Rentável</h3>
                    {analytics.mostProfitableService ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xl font-bold text-green-600">{analytics.mostProfitableService[0]}</p>
                          <p className="text-sm text-gray-600">
                            {analytics.mostProfitableService[1].toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </p>
                        </div>
                        <span className="text-4xl">💵</span>
                      </div>
                    ) : (
                      <p className="text-gray-500">Nenhum dado disponível</p>
                    )}
                  </div>
                </div>

                {/* Horários de Pico */}
                {analytics.peakHours.length > 0 && (
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">⏰ Horários de Pico</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {analytics.peakHours.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4">
                          <div>
                            <p className="text-sm text-gray-600">#{idx + 1} mais popular</p>
                            <p className="text-2xl font-bold text-indigo-600">{item.hour}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-3xl font-bold text-purple-600">{item.count}</p>
                            <p className="text-xs text-gray-600">agendamentos</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Gráfico Mensal */}
                {analytics.monthlyAppointments.length > 0 && (
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-6">📈 Performance dos Últimos 6 Meses</h3>
                    <div className="space-y-6">
                      {analytics.monthlyAppointments.map(({ month, monthName, completed, cancelled, revenue }) => {
                        const maxCompleted = Math.max(...analytics.monthlyAppointments.map(m => m.completed)) || 1;
                        const completedPercentage = (completed / maxCompleted) * 100;
                        
                        return (
                          <div key={month} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <p className="text-sm font-semibold text-gray-700 w-16">{monthName}</p>
                                <div className="flex items-center space-x-2 text-xs">
                                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded">
                                    ✓ {completed}
                                  </span>
                                  <span className="bg-red-100 text-red-700 px-2 py-1 rounded">
                                    ✗ {cancelled}
                                  </span>
                                </div>
                              </div>
                              <p className="text-sm font-bold text-green-600">
                                {revenue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                              </p>
                            </div>
                            <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                              <div
                                className="bg-gradient-to-r from-green-500 to-emerald-600 h-full rounded-full flex items-center justify-end pr-2 transition-all duration-500"
                                style={{ width: `${completedPercentage}%` }}
                              >
                                {completed > 0 && (
                                  <span className="text-white text-xs font-semibold">{completed}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Aba: Fila de Espera */}
            {activeTab === 'waitlist' && (
              <div className="bg-white rounded-xl shadow-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">🔔 Fila de Espera</h2>
                    <p className="text-gray-600 text-sm mt-1">
                      Clientes aguardando horários disponíveis
                    </p>
                  </div>
                </div>

                {Object.keys(waitlistData).length === 0 ? (
                  <div className="text-center py-16 bg-gray-50 rounded-lg">
                    <div className="text-6xl mb-4">🔔</div>
                    <p className="text-gray-500 text-lg mb-2">Nenhum cliente na fila de espera</p>
                    <p className="text-gray-400 text-sm">
                      Quando houver clientes aguardando, eles aparecerão aqui
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(waitlistData)
                      .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                      .map(([date, items]) => {
                        const waiting = items.filter(i => i.status === 'waiting');
                        const notified = items.filter(i => i.status === 'notified');
                        
                        return (
                          <div key={date} className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  {icons.calendar({ className: 'h-5 w-5 text-indigo-600' })}
                                  <h3 className="font-semibold text-gray-900">
                                    {new Date(date + 'T00:00:00').toLocaleDateString('pt-BR', { 
                                      weekday: 'long', 
                                      day: '2-digit', 
                                      month: 'long', 
                                      year: 'numeric' 
                                    })}
                                  </h3>
                                </div>
                                <div className="flex gap-4">
                                  <span className="text-sm text-gray-600">
                                    <strong>{waiting.length}</strong> aguardando
                                  </span>
                                  {notified.length > 0 && (
                                    <span className="text-sm text-green-600">
                                      <strong>{notified.length}</strong> notificados
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="divide-y divide-gray-100">
                              {items.map((item, index) => (
                                <div 
                                  key={item.id} 
                                  className={`px-4 py-4 hover:bg-gray-50 transition-colors ${
                                    item.status === 'notified' ? 'bg-green-50' : ''
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-3 flex-1">
                                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                        item.status === 'waiting' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                                      }`}>
                                        #{index + 1}
                                      </div>
                                      
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                          <h4 className="font-semibold text-gray-900">{item.userName}</h4>
                                          {item.status === 'notified' && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">
                                              {icons.check({ className: 'h-3 w-3 mr-1' })}
                                              Notificado
                                            </span>
                                          )}
                                        </div>
                                        
                                        <div className="space-y-1 text-sm text-gray-600">
                                          {item.userEmail && (
                                            <div className="flex items-center gap-2">
                                              {icons.mail({ className: 'h-4 w-4' })}
                                              <span className="truncate">{item.userEmail}</span>
                                            </div>
                                          )}
                                          {item.userPhone && (
                                            <div className="flex items-center gap-2">
                                              {icons.phone({ className: 'h-4 w-4' })}
                                              <span>{item.userPhone}</span>
                                            </div>
                                          )}
                                        </div>
                                        
                                        <div className="mt-2 flex flex-wrap gap-1">
                                          {item.preferredTimes.map(time => (
                                            <span 
                                              key={time}
                                              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700"
                                            >
                                              {icons.clock({ className: 'h-3 w-3 mr-1' })}
                                              {time}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <div className="flex-shrink-0 text-right">
                                      <p className="text-xs text-gray-500">
                                        Entrou em:
                                      </p>
                                      <p className="text-xs font-medium text-gray-700">
                                        {item.createdAt ? new Date(item.createdAt).toLocaleString('pt-BR', {
                                          day: '2-digit',
                                          month: '2-digit',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        }) : 'N/A'}
                                      </p>
                                      {item.notifiedAt && (
                                        <>
                                          <p className="text-xs text-green-600 mt-2">
                                            Notificado em:
                                          </p>
                                          <p className="text-xs font-medium text-green-700">
                                            {new Date(item.notifiedAt).toLocaleString('pt-BR', {
                                              day: '2-digit',
                                              month: '2-digit',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            })}
                                          </p>
                                        </>
                                      )}
                                      {/* Botões WhatsApp */}
                                      {item.userPhone && (
                                        <div className="mt-3 flex flex-col gap-1.5">
                                          <button
                                            type="button"
                                            onClick={() => openWhatsApp(
                                              item.userPhone,
                                              msgBarbershopReleasesSlot({
                                                clientName: item.userName,
                                                service: 'A combinar',
                                                date: date,
                                                time: item.preferredTimes?.[0] || 'A definir',
                                              })
                                            )}
                                            className="flex items-center gap-1 py-1 px-2 text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-300 rounded hover:bg-emerald-200 transition-colors"
                                            title="Notificar cliente de horário liberado via WhatsApp"
                                          >
                                            <span className="font-bold">WA</span> Liberar
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => openWhatsApp(
                                              item.userPhone,
                                              msgReleasedSlotExpired({
                                                clientName: item.userName,
                                                date: date,
                                                time: item.preferredTimes?.[0] || 'A definir',
                                              })
                                            )}
                                            className="flex items-center gap-1 py-1 px-2 text-xs font-medium bg-orange-100 text-orange-700 border border-orange-300 rounded hover:bg-orange-200 transition-colors"
                                            title="Avisar cliente que o horário liberado expirou"
                                          >
                                            <span className="font-bold">WA</span> Expirado
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    
                    {/* Informações Adicionais */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                      <div className="flex items-start gap-3">
                        {icons.info({ className: 'h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5' })}
                        <div className="text-sm text-blue-800">
                          <p className="font-semibold mb-2">Como funciona a Fila de Espera?</p>
                          <ul className="list-disc list-inside space-y-1 text-blue-700">
                            <li>Clientes são notificados automaticamente quando você adiciona horários</li>
                            <li>Notificações são enviadas por email e no sistema</li>
                            <li>Ordem de notificação: primeiro a entrar, primeiro a ser notificado</li>
                            <li>Até 5 pessoas são notificadas por horário disponível</li>
                            <li>Filas expiram automaticamente 24h após notificação ou quando a data passa</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Aba: Integrações */}
            {activeTab === 'integrations' && (
              <div className="bg-white rounded-xl shadow-xl p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Integrações</h2>
                
                <div className="space-y-6">
                  <GoogleCalendarConnect userId={currentUser.id} />
                  
                  {/* Placeholder para futuras integrações */}
                  <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <p className="text-gray-500 text-sm">
                      Mais integrações em breve...
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Sidebar: Ações */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-xl p-6 sticky top-24 space-y-4">
              <h3 className="text-xl font-bold text-gray-900">Ações Rápidas</h3>
              <Button variant="primary" className="w-full" onClick={() => setIsAddSlotModalOpen(true)}>
                {icons.plus({ className: 'h-5 w-5 mr-2' })}
                Adicionar Horário Vago
              </Button>
              <Button variant="secondary" className="w-full" onClick={() => setIsBulkSlotModalOpen(true)}>
                {icons.calendar({ className: 'h-5 w-5 mr-2' })}
                Cadastrar Horários do Mês
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal para Adicionar Horário */}
      <AddSlotModal
        isOpen={isAddSlotModalOpen}
        onClose={() => setIsAddSlotModalOpen(false)}
        onSubmit={handleAddSlot}
      />

      {/* Modal para Adicionar Horários em Lote */}
      <BulkSlotModal
        isOpen={isBulkSlotModalOpen}
        onClose={() => setIsBulkSlotModalOpen(false)}
        onSubmit={handleBulkAddSlots}
      />

      {/* Modal de Registro de Pagamento */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => { setIsPaymentModalOpen(false); setAppointmentToComplete(null); }}
        appointment={appointmentToComplete}
        onConfirm={handlePaymentConfirm}
      />
    </div>
  );
};

export default BarbershopOwnerPage;
